// src/pages/portal/Page_Styles/Page_Styles.js
// Editor de estilos (/estilos) — ordenado por ANATOMIA de la pagina.
// Elige un ambito (General o una pagina). El editor lee el manifiesto de
// anatomia (pageAnatomy.js) y monta, en el MISMO orden:
//   - el acordeon de edicion (centro), bloque a bloque = componentes reales,
//   - la vista previa DESCOMPUESTA (derecha), un espejo por bloque.
// Asi el diseñador edita "lo que ve". Los cambios se aplican en vivo via
// ThemeRuntimeProvider, se guardan en borrador local y como PROPUESTAS (JSON).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Collapse,
  ColorPicker,
  Drawer,
  Empty,
  Input,
  InputNumber,
  List,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  DownloadOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  SaveOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../hooks";
import { useThemeRuntime } from "../../../ui/theme/ThemeRuntimeProvider";
import { SEMANTIC_TOKENS } from "../../../ui/theme/semanticTokens";
import { TOKEN_CATALOG } from "../../../ui/theme/tokenCatalog";
import {
  DEFAULT_IMAGES,
  DEFAULT_TEXT_KEYS,
  SCOPE_TEXT_DEFAULTS,
} from "../../../ui/theme/styleDefaults";
import {
  SCOPE_OPTIONS,
  SCOPE_BASE,
  SCOPE_BRAND,
  scopeLabelKey,
} from "../../../ui/theme/pageScopes";
import { getAnatomy } from "../../../ui/theme/pageAnatomy";
import {
  buildTemplateData,
  dataToOverrides,
} from "../../../ui/theme/styleTemplateIO";
import { useStylesAccessStore } from "../../../store/stylesAccessStore";
import { StyleTemplatesApi } from "../../../api/styleTemplatesApi";
import { BlockPreview } from "./StylePreviews";
import styles from "./Page_Styles.module.scss";

const { Title, Text, Paragraph } = Typography;
const DRAFT_KEY = "edu.stylesEditor.draft.v1";
const api = new StyleTemplatesApi();

const STATUS_META = {
  draft: { color: "default", label: "Borrador" },
  proposed: { color: "green", label: "Propuesta" },
  approved: { color: "gold", label: "Aprobada" },
};

// ---- Clasificacion de tokens para elegir el control adecuado ----
const isColorToken = (t) =>
  t.startsWith("color.") || (t.startsWith("sidebar.") && !t.endsWith("Radius"));
const isRadiusToken = (t) => t.startsWith("radius.") || t.endsWith("Radius");
const isFontToken = (t) => t.startsWith("font.");

// Opciones de tipografia
const FONT_OPTIONS = [
  { value: "'Nunito', sans-serif", label: "Nunito" },
  { value: "'Roboto', sans-serif", label: "Roboto" },
  { value: "'Poppins', sans-serif", label: "Poppins" },
  { value: "'Montserrat', sans-serif", label: "Montserrat" },
  { value: "'Lato', sans-serif", label: "Lato" },
  { value: "'Inter', sans-serif", label: "Inter" },
  { value: "system-ui, sans-serif", label: "Sistema" },
];
const WEIGHT_OPTIONS = [
  { value: "400", label: "Normal" },
  { value: "500", label: "Media" },
  { value: "600", label: "Seminegrita" },
  { value: "700", label: "Negrita" },
  { value: "800", label: "Extra" },
];
const SCALE_OPTIONS = [
  { value: "0.9", label: "Pequeno" },
  { value: "1", label: "Normal" },
  { value: "1.1", label: "Grande" },
  { value: "1.25", label: "Muy grande" },
];

export function Page_Styles() {
  const navigate = useNavigate();
  const { t } = useTranslation(["scenes", "common", "assistants", "teachers"]);
  const { accessToken } = useAuth();
  const {
    overrides,
    pageOverrides,
    setTokenOverride,
    resetOverrides,
    applyOverrides,
    resolveCssVars,
  } = useThemeRuntime();

  const [hydrated, setHydrated] = useState(false);
  const [openKey, setOpenKey] = useState(null);
  const [hotCtrl, setHotCtrl] = useState(null);
  const [scope, setScope] = useState(SCOPE_BASE);

  const [templates, setTemplates] = useState([]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [busy, setBusy] = useState(false);

  // Acceso por grant de sesion (OTP). Sin grant no se entra al editor.
  const grant = useStylesAccessStore((s) => s.grant);
  const allowed = !!grant;
  const canApprove = useStylesAccessStore((s) => s.isApprover);

  const blocks = useMemo(() => getAnatomy(scope), [scope]);

  // Al cambiar de ambito, abrimos (y resaltamos) el primer bloque.
  useEffect(() => {
    setOpenKey(blocks[0]?.key || null);
  }, [scope, blocks]);

  useEffect(() => {
    if (!allowed || hydrated) return;
    try {
      const raw = window.localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        applyOverrides(parsed.base || {}, parsed.pages || {});
      }
    } catch (e) {
      // borrador corrupto: lo ignoramos
    }
    setHydrated(true);
  }, [allowed, hydrated, applyOverrides]);

  useEffect(() => {
    if (!hydrated) return;
    try {
      window.localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({ base: overrides, pages: pageOverrides }),
      );
    } catch (e) {
      // cuota / modo privado: no rompemos
    }
  }, [overrides, pageOverrides, hydrated]);

  const loadTemplates = useCallback(async () => {
    if (!accessToken) return;
    try {
      const data = await api.list(accessToken);
      setTemplates(data?.docs || []);
    } catch (e) {
      message.error(e?.message || "No se pudieron cargar las propuestas");
    }
  }, [accessToken]);

  useEffect(() => {
    if (allowed) loadTemplates();
  }, [allowed, loadTemplates]);

  // Ambito de almacenamiento: "Marca (global)" escribe en la capa base para que
  // cascade a toda la plataforma (no es una pagina propia).
  const storeScope = scope === SCOPE_BRAND ? SCOPE_BASE : scope;

  // Valor efectivo del token/slot en el ambito actual (pagina > base > default).
  const currentValue = useCallback(
    (token) => {
      if (storeScope !== SCOPE_BASE) {
        const pv = pageOverrides[storeScope]?.[token];
        if (pv != null && pv !== "") return pv;
      }
      return overrides[token] ?? SEMANTIC_TOKENS[token]?.value ?? "";
    },
    [storeScope, overrides, pageOverrides],
  );

  const setVal = useCallback(
    (token, value) => setTokenOverride(token, value, storeScope),
    [setTokenOverride, storeScope],
  );

  // Defecto real de un slot: texto via i18n (clave ns:key), imagen via asset.
  // El texto puede depender del ambito (pageTitle cambia por pagina).
  const defaultText = useCallback(
    (token, sc) => {
      const key = SCOPE_TEXT_DEFAULTS[sc]?.[token] || DEFAULT_TEXT_KEYS[token];
      return key ? t(key) : "";
    },
    [t],
  );
  const defaultImage = useCallback((token) => DEFAULT_IMAGES[token] || "", []);

  // Valor "efectivo" para los espejos: override o, si no hay, el defecto real.
  const slot = useCallback(
    (token) => {
      const v = currentValue(token);
      if (v) return v;
      if (token.startsWith("text.")) return defaultText(token, scope);
      if (token.startsWith("image.")) return defaultImage(token);
      return "";
    },
    [currentValue, defaultText, defaultImage, scope],
  );

  // Nombre legible de un ambito = nombre del menu (override base o i18n). Misma
  // fuente que el sidebar, asi el selector y el menu nunca divergen.
  const nameFor = useCallback(
    (sc) => {
      const opt = SCOPE_OPTIONS.find((s) => s.key === sc);
      if (opt?.label) return opt.label; // Marca (global), General
      const lk = opt?.labelKey || scopeLabelKey(sc);
      if (lk) {
        const ov = overrides[`text.${lk}`];
        if (typeof ov === "string" && ov.trim()) return ov;
        // nav.* vive en el namespace common; el ns por defecto aqui es scenes.
        return t(lk, { ns: "common" });
      }
      return sc;
    },
    [overrides, t],
  );

  // CSS vars del ambito activo, para que los espejos reflejen lo editado.
  const previewVars = useMemo(() => {
    const map =
      storeScope === SCOPE_BASE
        ? overrides
        : { ...overrides, ...(pageOverrides[storeScope] || {}) };
    return resolveCssVars(map);
  }, [storeScope, overrides, pageOverrides, resolveCssVars]);

  const handleReset = () => {
    resetOverrides();
    try {
      window.localStorage.removeItem(DRAFT_KEY);
    } catch (e) {
      // no-op
    }
    message.success("Estilos restablecidos a los valores actuales");
  };

  const handleDownload = () => {
    const json = JSON.stringify(
      {
        name: draftName || "Borrador",
        ...buildTemplateData(overrides, pageOverrides),
      },
      null,
      2,
    );
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "estilo-educa360.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSaveProposal = async () => {
    if (!draftName.trim()) {
      message.warning("Pon un nombre a la propuesta");
      return;
    }
    setBusy(true);
    try {
      await api.create(accessToken, {
        name: draftName.trim(),
        data: buildTemplateData(overrides, pageOverrides),
        status: "proposed",
      });
      message.success("Propuesta guardada para el equipo");
      setSaveOpen(false);
      setDraftName("");
      await loadTemplates();
    } catch (e) {
      message.error(e?.message || "No se pudo guardar la propuesta");
    } finally {
      setBusy(false);
    }
  };

  const handleApply = (tpl) => {
    const { base, pages } = dataToOverrides(tpl.data);
    applyOverrides(base, pages);
    message.success(`Aplicada: ${tpl.name}`);
    setGalleryOpen(false);
  };

  const handleApprove = async (tpl) => {
    setBusy(true);
    try {
      await api.approve(accessToken, tpl._id);
      message.success(`Aprobada: ${tpl.name}`);
      await loadTemplates();
    } catch (e) {
      message.error(e?.message || "No se pudo aprobar");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (tpl) => {
    setBusy(true);
    try {
      await api.remove(accessToken, tpl._id);
      message.success("Propuesta eliminada");
      await loadTemplates();
    } catch (e) {
      message.error(e?.message || "No se pudo eliminar");
    } finally {
      setBusy(false);
    }
  };

  // ---- Metadatos de un control (label/hint/appliesTo), con fallback al catalogo ----
  const controlMeta = (ctrl) => {
    const cat = ctrl.kind === "token" ? TOKEN_CATALOG[ctrl.token] : null;
    return {
      label: ctrl.label || cat?.label || ctrl.token || ctrl.slot,
      hint: ctrl.hint || cat?.hint || "",
      appliesTo: cat?.appliesTo || null,
    };
  };

  // ---- Control compuesto de tipografia (fuente + tamano + peso + cursiva) ----
  // role "display"/"body" usa tokens font.*; o se pasan tokens explicitos
  // (p.ej. el menu lateral). `noScale` oculta el tamano.
  const renderTypography = (ctrl) => {
    const role = ctrl.role;
    const tk = ctrl.tokens || {
      family: `font.${role}`,
      weight: `font.${role}Weight`,
      style: `font.${role}Style`,
      scale: `font.${role}Scale`,
    };
    const showScale = tk.scale && !ctrl.noScale;
    return (
      <div key={`typo.${role || tk.family}`} className={styles.typoGroup}>
        <div className={styles.rowLabel}>{ctrl.label}</div>
        {ctrl.hint ? <div className={styles.rowHint}>{ctrl.hint}</div> : null}
        <div className={styles.typoGrid}>
          <div className={styles.typoField}>
            <span className={styles.typoFieldLabel}>Fuente</span>
            <Select
              size="small"
              value={currentValue(tk.family)}
              onChange={(v) => setVal(tk.family, v)}
              options={FONT_OPTIONS}
              style={{ width: "100%" }}
            />
          </div>
          {showScale ? (
            <div className={styles.typoField}>
              <span className={styles.typoFieldLabel}>Tamano</span>
              <Select
                size="small"
                value={String(currentValue(tk.scale) || "1")}
                onChange={(v) => setVal(tk.scale, v)}
                options={SCALE_OPTIONS}
                style={{ width: "100%" }}
              />
            </div>
          ) : null}
          <div className={styles.typoField}>
            <span className={styles.typoFieldLabel}>Peso</span>
            <Select
              size="small"
              value={String(currentValue(tk.weight) || "400")}
              onChange={(v) => setVal(tk.weight, v)}
              options={WEIGHT_OPTIONS}
              style={{ width: "100%" }}
            />
          </div>
          <div className={styles.typoField}>
            <span className={styles.typoFieldLabel}>Cursiva</span>
            <Switch
              size="small"
              checked={currentValue(tk.style) === "italic"}
              onChange={(on) => setVal(tk.style, on ? "italic" : "normal")}
            />
          </div>
        </div>
      </div>
    );
  };

  // ---- Render de un control segun su tipo ----
  const renderControl = (ctrl, blockKey) => {
    if (ctrl.kind === "typography") return renderTypography(ctrl);
    const meta = controlMeta(ctrl);
    const key = ctrl.token || `${ctrl.kind}.${ctrl.slot}`;
    const isText = ctrl.kind === "text";

    let control = null;
    if (ctrl.kind === "image") {
      const token = `image.${ctrl.slot}`;
      const url = currentValue(token);
      const thumb = url || defaultImage(token);
      control = (
        <Space>
          {thumb ? (
            <img src={thumb} alt="" className={styles.imgPreview} />
          ) : null}
          <Input
            placeholder="URL de imagen (vacio = la actual)"
            value={url}
            onChange={(e) => setVal(token, e.target.value)}
            allowClear
            size="small"
            style={{ width: 170 }}
          />
        </Space>
      );
    } else if (ctrl.kind === "text") {
      const token = `text.${ctrl.slot}`;
      const val = currentValue(token);
      const ph = defaultText(token, scope) || "Vacio = texto por defecto";
      control =
        ctrl.type === "textarea" ? (
          <Input.TextArea
            value={val}
            onChange={(e) => setVal(token, e.target.value)}
            rows={2}
            placeholder={ph}
          />
        ) : (
          <Input
            value={val}
            onChange={(e) => setVal(token, e.target.value)}
            placeholder={ph}
          />
        );
    } else if (ctrl.kind === "choice") {
      control = (
        <Select
          size="small"
          value={currentValue(ctrl.token)}
          onChange={(v) => setVal(ctrl.token, v)}
          options={ctrl.options}
          style={{ minWidth: 150 }}
        />
      );
    } else if (ctrl.kind === "number") {
      const unit = ctrl.unit || "px";
      control = (
        <InputNumber
          min={ctrl.min ?? 0}
          max={ctrl.max ?? 200}
          value={parseInt(currentValue(ctrl.token), 10)}
          onChange={(n) => setVal(ctrl.token, `${n || 0}${unit}`)}
          size="small"
          formatter={(v) => `${v}${unit}`}
          parser={(v) => String(v).replace(unit, "")}
        />
      );
    } else {
      // token
      const token = ctrl.token;
      if (isColorToken(token)) {
        control = (
          <ColorPicker
            value={currentValue(token)}
            onChange={(c) => setVal(token, c.toHexString())}
            showText
            size="small"
          />
        );
      } else if (isRadiusToken(token)) {
        control = (
          <InputNumber
            min={0}
            max={40}
            value={parseInt(currentValue(token), 10)}
            onChange={(n) => setVal(token, `${n || 0}px`)}
            size="small"
            formatter={(v) => `${v}px`}
            parser={(v) => String(v).replace("px", "")}
          />
        );
      } else if (isFontToken(token)) {
        control = (
          <Input
            value={currentValue(token)}
            onChange={(e) => setVal(token, e.target.value)}
            size="small"
            style={{ maxWidth: 200 }}
          />
        );
      }
    }

    return (
      <div
        key={key}
        className={isText ? styles.rowCol : styles.row}
        onMouseEnter={() => setHotCtrl({ block: blockKey, id: key })}
        onMouseLeave={() => setHotCtrl((h) => (h && h.id === key ? null : h))}
      >
        <div className={styles.rowLabelBox}>
          <span className={styles.rowLabel}>
            {meta.label}
            {meta.appliesTo ? (
              <Tooltip
                title={
                  <div>
                    <div className={styles.tipTitle}>Donde se ve</div>
                    <ul className={styles.tipList}>
                      {meta.appliesTo.map((a) => (
                        <li key={a}>{a}</li>
                      ))}
                    </ul>
                  </div>
                }
              >
                <InfoCircleOutlined className={styles.infoIcon} />
              </Tooltip>
            ) : null}
          </span>
          {meta.hint ? (
            <span className={styles.rowHint}>{meta.hint}</span>
          ) : null}
        </div>
        {control}
      </div>
    );
  };

  const sectionItems = blocks.map((block) => ({
    key: block.key,
    label: (
      <div className={styles.blockHead}>
        <span className={styles.blockTitle}>{block.label}</span>
        {block.desc ? (
          <span className={styles.blockDesc}>{block.desc}</span>
        ) : null}
      </div>
    ),
    children: (
      <div>
        {block.controls.map((ctrl) => renderControl(ctrl, block.key))}
        {block.note ? (
          <div className={styles.blockNote}>{block.note}</div>
        ) : null}
      </div>
    ),
  }));

  if (!allowed) {
    return (
      <div className={styles.denied}>
        <Title level={4}>Acceso restringido</Title>
        <Paragraph type="secondary">
          El editor de estilos solo esta disponible para el equipo de diseno.
        </Paragraph>
        <Button onClick={() => navigate(-1)}>Volver</Button>
      </div>
    );
  }

  const baseDirty = Object.keys(overrides).length > 0;
  const pagesDirty = Object.values(pageOverrides).some(
    (m) => m && Object.keys(m).length > 0,
  );
  const dirty = baseDirty || pagesDirty;
  const scopeDirty =
    storeScope === SCOPE_BASE
      ? baseDirty
      : Object.keys(pageOverrides[storeScope] || {}).length > 0;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Editor de Estilos
          </Title>
          <Text type="secondary">
            Elige el ambito y edita por componente. General afecta a toda la
            plataforma; una pagina solo a esa pantalla. La vista previa muestra
            los componentes reales de esa pagina.
          </Text>
        </div>
        <Space wrap>
          <Tag color={dirty ? "green" : "default"}>
            {dirty ? "Cambios sin guardar" : "Sin cambios"}
          </Tag>
          <Button
            icon={<AppstoreOutlined />}
            onClick={() => setGalleryOpen(true)}
          >
            Propuestas
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            Restablecer
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleDownload}>
            JSON
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            disabled={!dirty}
            onClick={() => setSaveOpen(true)}
          >
            Guardar propuesta
          </Button>
        </Space>
      </header>

      <div className={styles.scopeBar}>
        <span className={styles.scopeLabel}>Editando:</span>
        <Select
          value={scope}
          onChange={setScope}
          size="small"
          style={{ minWidth: 180 }}
          options={SCOPE_OPTIONS.map((s) => ({
            value: s.key,
            label: nameFor(s.key),
          }))}
        />
        {scope === SCOPE_BRAND ? (
          <Tag color="warning" icon={<WarningOutlined />}>
            Afecta a TODA la plataforma
            {scopeDirty ? " (con cambios)" : ""}
          </Tag>
        ) : scope !== SCOPE_BASE ? (
          <Tag color={scopeDirty ? "blue" : "default"}>
            Solo afecta a {nameFor(scope)}
            {scopeDirty ? " (con cambios)" : ""}
          </Tag>
        ) : null}
      </div>

      <div className={styles.body}>
        <div className={styles.controls}>
          <Collapse
            activeKey={openKey || undefined}
            onChange={(keys) => {
              const arr = Array.isArray(keys) ? keys : [keys];
              setOpenKey(arr[arr.length - 1] || null);
            }}
            accordion
            items={sectionItems}
            expandIconPlacement="end"
          />
        </div>

        <div className={styles.previewWrap}>
          <Text type="secondary">Vista previa ({nameFor(scope)})</Text>
          <div className={styles.previewStack} style={previewVars}>
            {blocks.map((block) => (
              <BlockPreview
                key={block.key}
                block={block}
                hot={openKey === block.key}
                hotCtrl={hotCtrl}
                slot={slot}
              />
            ))}
          </div>
          <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
            Cada bloque de la izquierda ilumina su componente aqui. Navega a la
            pagina real para ver el resultado aplicado al 100%.
          </Paragraph>
        </div>
      </div>

      <Modal
        title="Guardar propuesta"
        open={saveOpen}
        onOk={handleSaveProposal}
        onCancel={() => setSaveOpen(false)}
        confirmLoading={busy}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Paragraph type="secondary">
          La propuesta (general + paginas) quedara visible para el equipo. No se
          aplica a produccion: eso lo hace un dev tras la aprobacion.
        </Paragraph>
        <Input
          placeholder="Nombre de la propuesta (p.ej. Verano 2026)"
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          onPressEnter={handleSaveProposal}
          maxLength={80}
        />
      </Modal>

      <Drawer
        title="Propuestas de estilo"
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        styles={{ wrapper: { width: 420 } }}
      >
        {templates.length === 0 ? (
          <Empty description="Aun no hay propuestas guardadas" />
        ) : (
          <List
            dataSource={templates}
            rowKey="_id"
            renderItem={(tpl) => {
              const meta = STATUS_META[tpl.status] || STATUS_META.draft;
              return (
                <List.Item
                  actions={[
                    <Button
                      key="apply"
                      type="link"
                      size="small"
                      onClick={() => handleApply(tpl)}
                    >
                      Abrir
                    </Button>,
                    canApprove && tpl.status !== "approved" ? (
                      <Button
                        key="approve"
                        type="link"
                        size="small"
                        icon={<CheckCircleOutlined />}
                        loading={busy}
                        onClick={() => handleApprove(tpl)}
                      >
                        Aprobar
                      </Button>
                    ) : null,
                    <Popconfirm
                      key="del"
                      title="Eliminar esta propuesta?"
                      okText="Eliminar"
                      cancelText="Cancelar"
                      onConfirm={() => handleDelete(tpl)}
                    >
                      <Button
                        type="link"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>,
                  ].filter(Boolean)}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        {tpl.name}
                        <Tag color={meta.color}>{meta.label}</Tag>
                      </Space>
                    }
                    description={
                      <span style={{ fontSize: 12 }}>
                        {tpl.authorEmail || "—"}
                      </span>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </Drawer>
    </div>
  );
}

export default Page_Styles;
